import type { HttpClient, RequestOptions } from '../client.js';
import {
  type GetMarketplaceContractParams,
  type ListMarketplaceMethodsData,
  type ListMarketplaceMethodsParams,
  type MarketplaceContractData,
  type MarketplaceMethodCallData,
  type MarketplaceMethodCallParams,
  getMarketplaceContractParamsSchema,
  listMarketplaceMethodsParamsSchema,
  marketplaceMethodCallParamsSchema,
  marketplaceMethodNameSchema,
} from '@spectrum-nodes/api-spec/models';
import { parseParams } from '../internals/validation.js';

/** Marketplace custom ABI discovery, reads, and simulations */
export class MarketplaceNamespace {
  constructor(private readonly client: HttpClient) {}

  /** List published marketplace methods (functions and events). */
  async listMethods(
    params: ListMarketplaceMethodsParams = {},
    options?: RequestOptions,
  ): Promise<ListMarketplaceMethodsData> {
    const validated = parseParams(listMarketplaceMethodsParamsSchema, params);
    return this.client.jsonRpc<ListMarketplaceMethodsData>(
      'listMarketplaceMethods',
      validated,
      options,
    );
  }

  /** Fetch one marketplace contract and its deployments by app id. */
  async getContract(appId: string, options?: RequestOptions): Promise<MarketplaceContractData> {
    const validated = parseParams(getMarketplaceContractParamsSchema, {
      appId,
    } satisfies GetMarketplaceContractParams);
    return this.client.jsonRpc<MarketplaceContractData>(
      'getMarketplaceContract',
      validated,
      options,
    );
  }

  /**
   * Call a published marketplace method by its JSON-RPC name
   * (`spectrum_<appId>_[<chain>_]<deploymentName>_<operation>_<abiName>`).
   * Operation is `call` (eth_call), `simulateCall` (eth_simulateV1), `getLogs`,
   * or reserved `write`. Concrete deployments resolve chain and address from
   * the method name. Template methods (no chain segment) require `chain` and
   * `address` in `params`.
   */
  async call(
    method: string,
    params: Partial<MarketplaceMethodCallParams> = {},
    options?: RequestOptions,
  ): Promise<MarketplaceMethodCallData> {
    const methodName = parseParams(marketplaceMethodNameSchema, method);
    const validated = parseParams(marketplaceMethodCallParamsSchema, params);
    return this.client.jsonRpc<MarketplaceMethodCallData>(methodName, validated, options);
  }
}
